/**
 * The cells an announced ability will select (PRD #793).
 *
 * A telegraph that draws "something is coming near here" is not counterplay. If
 * a player is asked to leave an area, the area has to be the one that will
 * actually be damaged, for the whole warning, which means the drawing has to be
 * derived from the same declared geometry resolution reads rather than from an
 * artist's guess at it.
 *
 * So this takes the frozen shape and anchor the wire carries and answers with
 * tiles. It is pure, it is presentation-free, and it is tested against the same
 * shape records the kernel selects with: when a shape's parameters are retuned,
 * the drawn footprint moves with them and nobody has to remember to update a
 * second copy.
 *
 * It is deliberately *cells* rather than a polygon. The renderer already draws
 * one tile diamond cheaply, a dimetric projection turns a world circle into an
 * ellipse nobody wants to hand-derive, and a tile grid is the unit a player
 * reads position in anyway.
 */

import type { AbilityShape } from '../sim/combat/abilityCatalog';
import type { TilePoint } from '../sim/mobile';

export interface FootprintAnchor {
  /** Where the caster stood when the aim froze. */
  origin: { x: number; y: number };
  /** The aimed point, for a shape that is placed rather than measured. */
  point?: { x: number; y: number };
}

/** Cells whose centre lies within this many tiles of the footprint edge count. */
const CELL_INCLUSION_SLACK = 0;

function unit(from: { x: number; y: number }, to: { x: number; y: number }) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy);
  if (!Number.isFinite(length) || length === 0) return null;
  return { x: dx / length, y: dy / length };
}

function scan(
  centre: { x: number; y: number },
  reach: number,
  covers: (x: number, y: number) => boolean,
): TilePoint[] {
  const cells: TilePoint[] = [];
  if (!Number.isFinite(centre.x) || !Number.isFinite(centre.y)) return cells;
  const span = Math.ceil(Math.max(0, reach)) + 1;
  const originX = Math.round(centre.x);
  const originY = Math.round(centre.y);
  for (let y = originY - span; y <= originY + span; y += 1) {
    for (let x = originX - span; x <= originX + span; x += 1) {
      if (covers(x, y)) cells.push({ x, y });
    }
  }
  return cells;
}

function distanceToSegment(
  point: { x: number; y: number },
  start: { x: number; y: number },
  end: { x: number; y: number },
): number {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;
  const progress =
    lengthSquared === 0
      ? 0
      : Math.max(
          0,
          Math.min(
            1,
            ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared,
          ),
        );
  return Math.hypot(
    point.x - (start.x + dx * progress),
    point.y - (start.y + dy * progress),
  );
}

/**
 * The cells this announced shape covers.
 *
 * A shape with nothing to draw on the ground answers with none: a self buff has
 * no area, and a shot in flight is answered by moving out of the arrow's way
 * rather than off a square, so neither owes the ground a marker.
 */
export function abilityFootprintCells(
  shape: AbilityShape,
  anchor: FootprintAnchor,
): TilePoint[] {
  if (!shape || typeof shape !== 'object') return [];
  const origin = anchor.origin;
  switch (shape.kind) {
    case 'self':
    case 'projectile':
      return [];
    case 'tile': {
      const point = anchor.point;
      if (!point) return [];
      return scan(point, shape.radiusTiles, (x, y) =>
        Math.hypot(x - point.x, y - point.y) <=
        shape.radiusTiles + CELL_INCLUSION_SLACK,
      );
    }
    case 'circle':
      return scan(origin, shape.radiusTiles, (x, y) => {
        const dx = Math.abs(x - origin.x);
        const dy = Math.abs(y - origin.y);
        const distance =
          shape.metric === 'chebyshev' ? Math.max(dx, dy) : Math.hypot(dx, dy);
        return distance <= shape.radiusTiles + CELL_INCLUSION_SLACK;
      });
    case 'line': {
      const direction = anchor.point && unit(origin, anchor.point);
      if (!direction) return [];
      const end = {
        x: origin.x + direction.x * shape.rangeTiles,
        y: origin.y + direction.y * shape.rangeTiles,
      };
      return scan(origin, shape.rangeTiles, (x, y) =>
        distanceToSegment({ x, y }, origin, end) <=
        shape.widthTiles / 2 + CELL_INCLUSION_SLACK,
      );
    }
    case 'dash': {
      const direction = anchor.point && unit(origin, anchor.point);
      if (!direction) return [];
      const end = {
        x: origin.x + direction.x * shape.distanceTiles,
        y: origin.y + direction.y * shape.distanceTiles,
      };
      return scan(origin, shape.distanceTiles, (x, y) =>
        distanceToSegment({ x, y }, origin, end) <=
        shape.contactRadiusTiles + CELL_INCLUSION_SLACK,
      );
    }
    case 'cone': {
      const direction = anchor.point && unit(origin, anchor.point);
      if (!direction) return [];
      const halfArc = Math.min(Math.PI, (shape.arcDegrees * Math.PI) / 360);
      return scan(origin, shape.rangeTiles, (x, y) => {
        const dx = x - origin.x;
        const dy = y - origin.y;
        const distance = Math.hypot(dx, dy);
        if (distance > shape.rangeTiles + CELL_INCLUSION_SLACK) return false;
        if (distance === 0) return true;
        const cosine = (dx * direction.x + dy * direction.y) / distance;
        return cosine >= Math.cos(halfArc) - 1e-9;
      });
    }
    default: {
      const exhaustive: never = shape;
      void exhaustive;
      return [];
    }
  }
}
